
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { validatePassword, getPasswordStrengthColor, getPasswordStrengthText } from "@/utils/passwordValidation";
// import { Captcha } from "@/components/ui/captcha";

// Custom validator for password strength
const strongPassword = (password: string) => {
  const result = validatePassword(password);
  return result.isStrong;
};

const securityFormSchema = z.object({
  currentPassword: z.string().min(6, "Password must be at least 6 characters."),
  newPassword: z.string().min(10, "Password must be at least 10 characters.")
    .refine(strongPassword, {
      message: "Password is not strong enough. It should be at least 10 characters and include a mix of letters, numbers, and symbols."
    }),
  confirmPassword: z.string().min(10, "Password must be at least 10 characters."),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export function SecurityForm() {
  const { user } = useAuth();
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: { warning: '', suggestions: [] },
    isStrong: false
  });
  // const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  // const [captchaError, setCaptchaError] = useState<string | undefined>(undefined);

  const form = useForm<z.infer<typeof securityFormSchema>>({
    resolver: zodResolver(securityFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Watch the new password field to update strength in real-time
  const newPassword = form.watch("newPassword");
  
  useEffect(() => {
    if (newPassword) {
      setPasswordStrength(validatePassword(newPassword));
    } else {
      setPasswordStrength({
        score: 0,
        feedback: { warning: '', suggestions: [] },
        isStrong: false
      });
    }
  }, [newPassword]);

  /* Commented out CAPTCHA-related functions
  const handleCaptchaVerify = (token: string | null) => {
    setCaptchaToken(token);
    if (token) {
      setCaptchaError(undefined);
    }
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
    setCaptchaError("CAPTCHA has expired, please verify again");
  };
  */

  async function onSubmit(values: z.infer<typeof securityFormSchema>) {
    /* Commented out CAPTCHA validation
    if (!captchaToken) {
      setCaptchaError("Please verify that you are not a robot");
      return;
    }
    */

    try {
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword
      });

      if (error) throw error;

      toast.success("Password updated successfully");
      form.reset();
      // setCaptchaToken(null);
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error("Failed to update password");
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="currentPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Current Password</FormLabel>
              <FormControl>
                <Input type="password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>New Password</FormLabel>
              <FormControl>
                <Input type="password" {...field} />
              </FormControl>
              
              {/* Password strength indicator */}
              {newPassword && (
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span>Password strength:</span>
                    <span className={`font-semibold ${passwordStrength.score >= 3 ? 'text-green-600' : 'text-orange-600'}`}>
                      {getPasswordStrengthText(passwordStrength.score)}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getPasswordStrengthColor(passwordStrength.score)}`} 
                      style={{ width: `${(passwordStrength.score + 1) * 20}%` }}
                    />
                  </div>
                  
                  {/* Password feedback */}
                  {passwordStrength.feedback.warning && (
                    <p className="text-orange-600 text-xs mt-1">{passwordStrength.feedback.warning}</p>
                  )}
                  {passwordStrength.feedback.suggestions.length > 0 && (
                    <FormDescription className="mt-1">
                      <ul className="text-xs list-disc ml-4">
                        {passwordStrength.feedback.suggestions.map((suggestion, i) => (
                          <li key={i}>{suggestion}</li>
                        ))}
                      </ul>
                    </FormDescription>
                  )}
                </div>
              )}
              
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm New Password</FormLabel>
              <FormControl>
                <Input type="password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* Commented out CAPTCHA component
        <div className="space-y-2 mt-4">
          <Captcha 
            onVerify={handleCaptchaVerify} 
            onExpire={handleCaptchaExpire}
          />
          {captchaError && (
            <p className="text-red-500 text-xs mt-1">{captchaError}</p>
          )}
        </div>
        */}
        
        <Button type="submit" className="w-full bg-black hover:bg-black/90">
          Update Password
        </Button>
      </form>
    </Form>
  );
}
